import { useEffect, useRef, useState } from "react";
import { Eyebrow } from "./design";

// Hörproben — static audio examples (no TTS backend). Drop MP3 files into
// frontend/public/audio/ with the file names below; the play button appears
// automatically for every file that exists (checked via HEAD request), the
// rest show a quiet "Hörprobe folgt" chip. One shared <audio> element —
// starting a sample stops the previous one.

type Sample = {
  file: string; // /audio/<file>
  title: string;
  type: string;
  accent: string;
  preview: string;
};

const SAMPLES: Sample[] = [
  {
    file: "kriegsjahre.mp3",
    title: "Die Kriegsjahre von Heinrich M.",
    type: "Hörbuch",
    accent: "var(--oxblood)",
    preview:
      "Eine bewegende Geschichte über Überleben, Hoffnung und den Neuanfang nach dem Krieg — erzählt mit der Stimme eines Zeitzeugen.",
  },
  {
    file: "kochrezepte.mp3",
    title: "Großmutters Kochrezepte",
    type: "Podcast",
    accent: "var(--sage)",
    preview:
      "Mehr als nur Rezepte: eine Reise durch drei Generationen voller Familientraditionen, Küchendüfte und unvergessener Momente.",
  },
  {
    file: "handwerk.mp3",
    title: "50 Jahre Handwerk",
    type: "Buchausschnitt",
    accent: "var(--ochre)",
    preview:
      "Die Hände eines Schreiners erzählen ihre eigene Geschichte. Jede Narbe ein Kapitel, jedes Möbelstück ein Vermächtnis.",
  },
];

export default function Hoerproben() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [available, setAvailable] = useState<Record<string, boolean>>({});
  const [playing, setPlaying] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        SAMPLES.map(async (s) => {
          try {
            const r = await fetch(`/audio/${s.file}`, { method: "HEAD" });
            const ct = r.headers.get("content-type") || "";
            // SPA fallback returns index.html for missing files — require audio.
            return [s.file, r.ok && !ct.includes("text/html")] as const;
          } catch {
            return [s.file, false] as const;
          }
        }),
      );
      if (!cancelled) setAvailable(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
      audioRef.current?.pause();
    };
  }, []);

  function toggle(file: string) {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener("ended", () => setPlaying(null));
    }
    const audio = audioRef.current;
    if (playing === file) {
      audio.pause();
      setPlaying(null);
      return;
    }
    audio.src = `/audio/${file}`;
    audio
      .play()
      .then(() => setPlaying(file))
      .catch(() => setPlaying(null));
  }

  // Hide the whole section only if we know nothing is available AND no file
  // check has finished yet? No — always render; cards degrade gracefully.
  return (
    <section
      className="paper-bg-dark"
      style={{
        padding: "36px 22px 40px",
        borderTop: "1px solid rgba(43,26,15,0.08)",
        borderBottom: "1px solid rgba(43,26,15,0.08)",
      }}
    >
      <Eyebrow>Hörproben</Eyebrow>
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 400,
          fontSize: 28,
          lineHeight: 1.1,
          margin: "0 0 20px",
          color: "var(--ink)",
        }}
      >
        So klingen <em>fertige Geschichten</em>.
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 14,
        }}
      >
        {SAMPLES.map((s) => {
          const isPlaying = playing === s.file;
          const hasAudio = available[s.file];
          return (
            <div
              key={s.file}
              style={{
                background: "var(--paper-50)",
                border: "1px solid rgba(43,26,15,0.10)",
                borderTop: `4px solid ${s.accent}`,
                borderRadius: 12,
                padding: "16px 16px 14px",
                boxShadow: "0 4px 12px rgba(43,26,15,0.06)",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--f-ui)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--ink-faint)",
                }}
              >
                {s.type}
              </div>
              <div
                style={{
                  fontFamily: "var(--f-display)",
                  fontWeight: 500,
                  fontSize: 17,
                  lineHeight: 1.2,
                  color: "var(--ink)",
                }}
              >
                {s.title}
              </div>
              <p
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: "var(--ink-soft)",
                  margin: 0,
                  flex: 1,
                }}
              >
                {s.preview}
              </p>
              {hasAudio ? (
                <button
                  type="button"
                  onClick={() => toggle(s.file)}
                  aria-label={
                    isPlaying ? `${s.title} pausieren` : `${s.title} abspielen`
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    alignSelf: "flex-start",
                    background: isPlaying ? "var(--oxblood)" : "transparent",
                    color: isPlaying ? "var(--paper-50)" : "var(--oxblood)",
                    border: "1.5px solid var(--oxblood)",
                    borderRadius: 999,
                    padding: "8px 16px",
                    fontFamily: "var(--f-ui)",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  {isPlaying ? "Pause" : "Anhören"}
                </button>
              ) : (
                <span
                  style={{
                    alignSelf: "flex-start",
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 12.5,
                    color: "var(--ink-faint)",
                  }}
                >
                  Hörprobe folgt in Kürze.
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
    </svg>
  );
}
